import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate, useParams, useLocation } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

import Dashboard from "./components/Dashboard";
import FormBuilder from "./components/FormBuilder";
import FormEntryManager from "./components/FormEntryManager";
import PublicFormViewer from "./components/PublicFormViewer";
import { apiService } from "./services/apiService";
import "./App.css";

// --- WRAPPER COMPONENT TO HANDLE LOGIC ---
const AppContent = () => {
  const [forms, setForms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // --- FETCH FORMS FROM DB AND CALCULATE ENTRIES MANUALLY ---
  const loadForms = async () => {
    try {
      // 1. Fetch the forms with response counts directly from the backend
      const data = await apiService.fetchForms();
      setForms(data);
    } catch (err) {
      console.error("Fetch forms failed", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadForms();
  }, []);

  // --- ADMIN ACTIONS ---
  const handleOpenBuilder = (formId = null) => {
    if (formId) navigate(`/builder/${formId}`);
    else navigate("/builder/new");
  };

  const handleSaveForm = async (formData, selectedFormId) => {
    try {
      if (selectedFormId) {
        await apiService.updateForm(selectedFormId, formData);
      } else {
        await apiService.createForm(formData);
      }
      await loadForms();
      navigate("/"); // Back to Dashboard
    } catch (err) {
      console.error("Save form error:", err);
      alert("Failed to save form to backend.");
    }
  };

  const handleDeleteForm = async (id) => {
    try {
      await apiService.deleteForm(id);
      await loadForms();
    } catch (err) {
      alert("Failed to delete form.");
    }
  };

  const handleOpenManager = (id) => {
    navigate(`/manager/${id}/report`);
  };

  // --- NEW UPDATE: HANDLE REPORT VIEW ---
  const handleOpenReport = (id) => {
    navigate(`/manager/${id}/report`);
  };

  const handleBackToDashboard = () => {
    navigate("/");
  };

  if (isLoading) {
    return (
      <div className="d-flex min-vh-100 align-items-center justify-content-center bg-light">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status"></div>
          <p className="text-muted fw-medium">Loading Application...</p>
        </div>
      </div>
    );
  }

  // --- HIDE NAVBAR FOR PUBLIC VIEWS ---
  const isPublicView = location.pathname.startsWith("/form/");

  return (
    <div className="app-container bg-light min-vh-100 shadow-sm">
      {!isPublicView && (
        <nav
          className="navbar sticky-top mb-0"
          style={{
            zIndex: 1000,
            padding: "0.75rem 0",
            background: "rgba(245, 245, 245, 0.8)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.03)",
            minHeight: "66px",
            display: "flex",
            alignItems: "center"
          }}
        >
          <div className="container-fluid px-4 px-lg-5">
            <span
              className="navbar-brand d-flex align-items-center gap-3 fw-bold mb-0"
              style={{
                cursor: "pointer",
                letterSpacing: "-0.02em",
                fontSize: "1.35rem",
              }}
              onClick={handleBackToDashboard}
              // Group hover effect logic
              onMouseEnter={(e) => {
                const icon = e.currentTarget.querySelector('.logo-icon');
                const text = e.currentTarget.querySelector('.brand-text');

              }}
              onMouseLeave={(e) => {
                const icon = e.currentTarget.querySelector('.logo-icon');
                const text = e.currentTarget.querySelector('.brand-text');


              }}
            >
              {/* REPLACED CODE-LOGO WITH IMAGE LOGO */}
              {/* Kept class 'logo-icon' so the hover animation still works */}
              <img
                src="/icon.png"
                alt="Logo"
                className="logo-icon rounded-3 shadow-sm"
                style={{
                  width: "36px",
                  height: "36px",
                  objectFit: "contain", // Ensures the image doesn't get distorted
                  // Kept the bouncy transition
                }}
              />

              {/* Brand Typography with Smooth Letter Spacing Transition */}
              <span
                className="brand-text "
                style={{
                  fontSize: "24px",

                  display: "inline-block"
                }}
              >
                Form{" "}<span style={{ color: " #4FA63A", fontSize: "24px" }}>Manager</span>
              </span>
            </span>
          </div>
        </nav>
      )}

      <main>
        <Routes>
          {/* DASHBOARD */}
          <Route path="/" element={
            <Dashboard
              forms={forms}
              onCreate={() => handleOpenBuilder(null)}
              onEdit={handleOpenBuilder}
              onDelete={handleDeleteForm}
              onOpen={handleOpenManager}
              onViewReport={handleOpenReport} // PASSING THE NEW PROP HERE
            />
          } />

          {/* BUILDER (NEW & EDIT) */}
          <Route path="/builder/:id" element={<BuilderWrapper onSave={handleSaveForm} onCancel={handleBackToDashboard} />} />

          {/* MANAGER & REPORT */}
          <Route path="/manager/:id/report" element={<ManagerWrapper onBack={handleBackToDashboard} onUpdateForm={loadForms} />} />

          {/* PUBLIC VIEW */}
          <Route path="/form/:id" element={<PublicWrapper />} />
        </Routes>
      </main>
    </div>
  );
};

// --- WRAPPERS TO HANDLE PARAMS & LAZY LOADING ---

const BuilderWrapper = ({ onSave, onCancel }) => {
  const { id } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(id !== "new");

  useEffect(() => {
    if (id !== "new") {
      apiService.fetchForm(id).then(data => {
        setForm(data);
        setLoading(false);
      }).catch(err => {
        console.error("Failed to fetch form for builder", err);
        setLoading(false);
      });
    }
  }, [id]);

  if (loading) return (
    <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
      <div className="spinner-border text-success" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );

  const formId = id === "new" ? null : Number(id);
  return <FormBuilder initialData={form} onSave={(data) => onSave(data, formId)} onCancel={onCancel} />;
};

const ManagerWrapper = ({ onBack, onUpdateForm }) => {
  const { id } = useParams();
  const [data, setData] = useState({ form: null, entries: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiService.fetchForm(id),
      apiService.fetchEntries(id)
    ]).then(([form, entries]) => {
      setData({ form, entries });
      setLoading(false);
    }).catch(err => {
      console.error("Failed to fetch data for manager", err);
      setLoading(false);
    });
  }, [id]);

  if (loading) return (
    <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
      <div className="spinner-border text-success" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );
  if (!data.form) return <div className="p-5 text-center text-danger">Form not found.</div>;

  return <FormEntryManager form={data.form} initialEntries={data.entries} onBack={onBack} onUpdateForm={onUpdateForm} />;
};

const PublicWrapper = () => {
  const { id } = useParams();
  const [data, setData] = useState({ form: null, entries: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiService.fetchForm(id),
      apiService.fetchEntries(id)
    ]).then(([form, entries]) => {
      setData({ form, entries });
      setLoading(false);
    }).catch(err => {
      console.error("Failed to fetch data for viewer", err);
      setLoading(false);
    });
  }, [id]);

  if (loading) return (
    <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
      <div className="spinner-border text-success" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );
  if (!data.form) return <div className="p-5 text-center text-danger">Form not found.</div>;

  return <PublicFormViewer form={data.form} initialEntries={data.entries} />;
};

const App = () => (
  <Router>
    <AppContent />
  </Router>
);

export default App;